import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Stack,
  Box,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import { downloadFileFromUrl } from '../../../../common/logic/FileDownloader';
import { GeneralButton } from '../../../../common/ui/GeneralButton';
import {serviceModulesApi} from "../../../../api/serviceModulesApi";
import {useEffect, useState} from "react";

type Props = {
  open: boolean;
  onClose: () => void;
  name: string;
  filename: string;
  fileUrl: string | null;
  serviceModuleId: number;
  needsReadReceipt: boolean;
};

type ReadStatus = {
    employeeId: string;
    firstName: string;
    lastName: string;
    readAt: string | null;
    isRead: boolean;
}

const FileDialog = ({ open, onClose, name, filename, fileUrl, serviceModuleId, needsReadReceipt }: Props) => {
  const { t } = useTranslation();

    const [readStatuses, setReadStatuses] = useState<ReadStatus[]>([]);
    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        if (open) {
            loadReadStatuses(0);
        }
    }, [open]);

    const loadReadStatuses = async (targetPage: number) => {
        try {
            const response = await serviceModulesApi.getReadReceipt(
                serviceModuleId,
                {
                    offset: targetPage * 10,
                    limit: 10,
                }
            );

            if (!response.success() || !response.result) {
                setReadStatuses([]);
                return;
            }

            setReadStatuses(response.result.items);
            setTotalCount(response.result.totalCount);
            setPage(targetPage);

        } catch (err) {
            console.error("Error while loading:", err);
            setReadStatuses([]);
        }
    };

  const isPdf = fileUrl?.endsWith('.pdf');

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{name}</DialogTitle>
      <DialogContent
        dividers
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            padding: 3,
        }}
      >
        {fileUrl && (
          <Stack spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="body2">
              {t('serviceModulesPage.folderServicePage.existingFile')} {filename}
            </Typography>
            {isPdf ? (
              <Button
                variant="text"
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('serviceModulesPage.folderServicePage.openPdf')}
              </Button>
            ) : (
              <Box
                sx={{
                  maxWidth: 500,
                  maxHeight: 300,
                  overflow: 'hidden',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#f8f8f8',
                  borderRadius: 1,
                }}
              >
                <img
                  src={fileUrl}
                  alt="Uploaded"
                  style={{
                    width: 'auto',
                    height: 'auto',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            )}
          </Stack>
        )}
          {needsReadReceipt && (
              <Box sx={{ width: "100%", mt: 2 }}>
                  <ReadStatusHeader t={t}/>

                  {readStatuses.length === 0 ? (
                      <Typography variant="body2">
                          {t("serviceModulePage.noDataFound")}
                      </Typography>
                  ) : (
                      readStatuses.map((item) => (
                          <ReadStatusRow key={item.employeeId} item={item} t={t} />
                      ))
                  )}

                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                      <Button
                          disabled={page === 0}
                          onClick={() => loadReadStatuses(page - 1)}
                      >
                          {t("label.back")}
                      </Button>

                      <Typography>
                          {t("serviceModulesPage.page")} {page + 1} {t("serviceModulesPage.of")} {Math.ceil(totalCount / 10)}
                      </Typography>

                      <Button
                          disabled={(page + 1) * 10 >= totalCount}
                          onClick={() => loadReadStatuses(page + 1)}
                      >
                          {t("label.next")}
                      </Button>
                  </Box>
              </Box>
          )}
      </DialogContent>
      <DialogActions>
        {fileUrl && (
          <GeneralButton
            appVariant="primary"
            startIcon={<DownloadIcon />}
            onClick={async () => {
              try {
                await downloadFileFromUrl(fileUrl, filename);
              } catch (error) {
                console.error("Download error", error);
              }
            }}
          >
            {t("buttons.download")}
          </GeneralButton>
        )}

        <GeneralButton
          appVariant="secondary"
          onClick={onClose}
        >
          {t("buttons.close")}
        </GeneralButton>
      </DialogActions>
    </Dialog>
  );
};

export default FileDialog

function ReadStatusHeader({t}: {t: (key: string) => string}) {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "1fr 160px 140px",
                fontWeight: 600,
                borderBottom: "1px solid #e0e0e0",
                pb: 0.8,
                mb: 0.8,
            }}
        >
            <div>{t("serviceModulesPage.employeeName")}</div>
            <div>{t("serviceModulesPage.timestamp")}</div>
            <div>{t("serviceModulesPage.status")}</div>
        </Box>
    );
}

function ReadStatusRow({item, t}: { item: ReadStatus, t: (key: string) => string }) {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "1fr 180px 140px",
                py: 0.5,
                borderBottom: "1px solid #f0f0f0",
                alignItems: "center",
            }}
        >
            <div>
                {item.firstName} {item.lastName}
            </div>

            <div>
                {item.readAt
                    ? new Date(item.readAt + "Z").toLocaleString()
                    : '—'}
            </div>

            <div>
                <Box
                    sx={{
                        display: "inline-block",
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: 12,
                        color: "white",
                        backgroundColor: item.isRead ? "green" : "red",
                    }}
                >
                    {item.isRead ? t("serviceModulesPage.read") : t("serviceModulesPage.unread")}
                </Box>
            </div>
        </Box>
    );
}
